import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HiOutlineSearch } from "react-icons/hi";
import { Input } from "../../components/Input";
import { Table, ColumnType } from "../../components/Table";
import CustomSwitch from "../../components/Switch";
import { useDebounce } from "@/hooks/useDebounce";
import { CreateGroup } from "./CreateGroup";
import { CreateStore } from "./CreateStore";
import { useCompanyStore } from "@/store/companyStore";
import {
  ApiResponse,
  ECOMPANY_BUSINESS,
  GetStoresResp,
  RoleManagerExcute,
} from "@/types";
import { KEYS_SEARCH_PARAMS } from "./CompanyDetail";
import { Pagination } from "@/components";
import { EditStore } from "./EditStore";
import { useShopStore } from "@/store/shopStore";
import { DeleteStore } from "./DeleteStore";
import { EditGroup, EditGroupFormData } from "./EditGroup";
import { useGroupStore } from "@/store/groupStore";
import { useLoadingStore } from "@/store/loadingStore";
import { RoleEnum } from "@/types/enum";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils";

export const StoreList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const params = Object.fromEntries([...searchParams]);

  const navigate = useNavigate();
  const { fetchMyStores, stores, setStores, company } = useCompanyStore();
  const { updateStoreStatus } = useShopStore();
  const { fetchGroup } = useGroupStore();
  const { setLoading } = useLoadingStore();
  const { role } = useAuth();

  // Local state
  const [openEditGroup, setOpenEditGroup] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [searchOfficesInput, setSearchOfficesInput] = useState(
    params[KEYS_SEARCH_PARAMS.SEARCH_STORE] || "",
  );
  const [currentGroup, setCurrentGroup] = useState<
    (EditGroupFormData & { id: number }) | null
  >(null);

  // Debounce search terms
  const debouncedSearchOffices = useDebounce(searchOfficesInput, 500);
  const currentPageStore = Number(params[KEYS_SEARCH_PARAMS.PAGE_STORE]) || 1;
  const currentSearchStore = params[KEYS_SEARCH_PARAMS.SEARCH_STORE] || "";
  const isCompanyDisabled = company?.status === "DISABLE";
  // company have special business is TS_RETAIL or CS_RETAIL
  const isCompanySpecialBusiness = company?.business
    ? [ECOMPANY_BUSINESS.TS_RETAIL, ECOMPANY_BUSINESS.CS_RETAIL].includes(
        company?.business as ECOMPANY_BUSINESS,
      )
    : true;

  const getGroups = async (id: number) => {
    if (!id) return;
    setCurrentGroup(null);
    setLoading(true);
    const resp = await fetchGroup(id);
    setLoading(false);
    if (resp?.data) {
      setCurrentGroup({
        id,
        name: resp.data.name,
        stores: resp.data.stores.map((store) => store.id),
      });
      setOpenEditGroup(true);
    }
  };

  const handleUpdateStoreStatus = async (id: number) => {
    const oldStore = stores;
    const newStores = {
      ...stores,
      data: stores?.data.map((store) =>
        store.id === id
          ? {
              ...store,
              status: store.status === "ENABLE" ? "DISABLE" : "ENABLE",
            }
          : store,
      ),
    } as unknown as ApiResponse<GetStoresResp[]>;
    setStores(newStores);
    try {
      await updateStoreStatus(id);
    } catch (error: any) {
      setStores(oldStore);
    }
  };

  // Sales Offices Table Columns
  const salesOfficesColumns: ColumnType<GetStoresResp>[] = useMemo(
    () => [
      {
        title: (
          <div className="flex items-center justify-between gap-2">
            <p className="truncate">{company?.store_display_name || ""}</p>
          </div>
        ),
        dataIndex: "name",
        key: "name",
        width: "25%",
        visible: true,
        render: (value: string) => (
          <p className="text-sm text-social_linkedin leading-5.5 truncate">
            {value}
          </p>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-between gap-2">
            <p>住所</p>
          </div>
        ),
        dataIndex: "address",
        key: "address",
        width: "28%",
        render: (value: string) => (
          <p
            className="block max-w-xs truncate whitespace-nowrap overflow-hidden"
            title={value}
          >
            {value || ""}
          </p>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-between gap-2">
            <p>グループ</p>
          </div>
        ),
        dataIndex: "group",
        key: "group",
        width: "28%",
        render: (value: { id: number; name: string }) => {
          return (
            <p
              className={cn(
                "text-sm text-social_linkedin leading-5.5 truncate w-fit max-w-full",
                !RoleManagerExcute.GROUP.UPDATE[role || RoleEnum.OTHER]
                  ? "!text-charleston_green"
                  : "",
              )}
              onClick={(e) => {
                if (company?.status == "DISABLE") return;
                if (!RoleManagerExcute.GROUP.UPDATE[role || RoleEnum.OTHER])
                  return;
                e.stopPropagation();
                getGroups(value?.id || 0);
              }}
            >
              {value?.name}
            </p>
          );
        },
      },
      {
        title: "管理",
        key: "actions",
        width: "19%",
        visible: !isCompanySpecialBusiness,
        render: (_: any, record: GetStoresResp) => (
          <div className="flex items-center justify-center gap-x-5">
            {/* update store status */}
            <CustomSwitch
              disabled={
                isCompanyDisabled ||
                !RoleManagerExcute.STORE.ACTIVE[role || RoleEnum.OTHER]
              }
              checked={isCompanyDisabled ? false : record.status === "ENABLE"}
              onCheckedChange={() => handleUpdateStoreStatus(record.id)}
              size="xs"
            />
            {/* edit store */}
            <EditStore
              id={record?.id || 0}
              defaultValues={{
                name: record?.name || "",
                group: record?.group?.id || 0,
                address: record?.address || "",
                phone: record?.phone || "",
                report_history: record?.report_history || "",
                store_inspection_history:
                  record?.store_inspection_history || "",
              }}
              disabled={
                isCompanyDisabled ||
                !RoleManagerExcute.STORE.UPDATE[role || RoleEnum.OTHER]
              }
            />
            {/* delete store */}
            <DeleteStore
              id={record?.id || 0}
              disabled={
                isCompanyDisabled ||
                !RoleManagerExcute.STORE.CREATE[role || RoleEnum.OTHER]
              }
              resetSearch={resetSearch}
            />
          </div>
        ),
      },
    ],
    [stores, company?.status, company?.store_display_name],
  );

  const handleFetchStores = async (page?: number, s?: string) => {
    try {
      setLoadingStores(true);
      await fetchMyStores({
        page: page || 1,
        s: s,
        limit: 8,
      });
    } catch (error) {
      // TODO
    } finally {
      setLoadingStores(false);
    }
  };

  useEffect(() => {
    handleFetchStores(currentPageStore, currentSearchStore);
  }, [currentPageStore, currentSearchStore]);

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const resetSearch = () => {
    updateSearchParams({
      [KEYS_SEARCH_PARAMS.SEARCH_STORE]: "",
      [KEYS_SEARCH_PARAMS.PAGE_STORE]: "1",
    });
    setSearchOfficesInput("");
  };

  // Handle key down for search inputs
  const handleOfficesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchOfficesInput(searchOfficesInput);
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (debouncedSearchOffices?.trim()) {
      updateSearchParams({
        [KEYS_SEARCH_PARAMS.SEARCH_STORE]: debouncedSearchOffices?.trim() || "",
        [KEYS_SEARCH_PARAMS.PAGE_STORE]: "1",
      });
    } else {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH_STORE]: "" });
    }
  }, [debouncedSearchOffices]);

  return (
    <section className="bg-white rounded-lg shadow-shadow_light_card h-[29.75rem] flex flex-col">
      <div className="flex items-center justify-between py-2.5 px-4 gap-4">
        {/* Nhóm bên trái: tiêu đề + ô search */}
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <h3 className="text-lg font-semibold whitespace-nowrap max-w-[40%] truncate">
            {`${company?.store_display_name || ""}一覧`}
          </h3>
          <div className="flex-1" />
          <Input
            placeholder="検索"
            value={searchOfficesInput}
            onChange={(e) => setSearchOfficesInput(e.target.value)}
            className="max-w-80 !h-9 !rounded-lg"
            icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
            onKeyDown={handleOfficesKeyDown}
            containerClassName=""
          />
        </div>

        {/* Nhóm bên phải: nút bấm */}
        {RoleManagerExcute.GROUP.CREATE[role || RoleEnum.OTHER] &&
          !isCompanySpecialBusiness && <CreateGroup />}
        {RoleManagerExcute.STORE.CREATE[role || RoleEnum.OTHER] &&
          !isCompanySpecialBusiness && (
            <CreateStore resetSearch={resetSearch} />
          )}
      </div>
      <Table<GetStoresResp>
        columns={salesOfficesColumns}
        dataSource={stores?.data || []}
        loading={loadingStores}
        rowKey="id"
        bordered={true}
        size="middle"
        className={`border-l-0 border-r-0 rounded-b-none visible-scrollbar ${
          (stores?.data?.length || 0) > 0 ? "" : "border-b-0"
        }`}
        onRow={(record) => {
          return {
            onClick: () => {
              if (isCompanyDisabled) return;
              navigate(`${ROUTES.STORE}/${record.id}`);
            },
          };
        }}
      />
      <div className="flex-1" />
      <Pagination
        currentPage={currentPageStore}
        totalPages={stores?.pagination?.pageCount || 0}
        onPageChange={(page: number) => {
          updateSearchParams({
            [KEYS_SEARCH_PARAMS.PAGE_STORE]: page.toString(),
          });
        }}
      />

      {currentGroup && (
        <EditGroup
          id={currentGroup.id}
          defaultValues={currentGroup}
          open={openEditGroup}
          setOpen={setOpenEditGroup}
        />
      )}
    </section>
  );
};
